import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface SubscriptionData {
  subscribed: boolean;
  product_id?: string;
  subscription_end?: string;
  manual_override?: boolean;
}

interface CachedSubscriptionData extends SubscriptionData {
  timestamp: number;
}

const CACHE_KEY = "premium_status_cache";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const usePremiumStatus = () => {
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [justBecamePremium, setJustBecamePremium] = useState(false);

  const loadFromCache = (): SubscriptionData | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;
      
      const data: CachedSubscriptionData = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - data.timestamp < CACHE_DURATION) {
        return {
          subscribed: data.subscribed,
          product_id: data.product_id,
          subscription_end: data.subscription_end,
          manual_override: data.manual_override,
        };
      }
      
      return null;
    } catch {
      return null;
    }
  };

  const saveToCache = (data: SubscriptionData) => {
    try {
      const cached: CachedSubscriptionData = {
        ...data,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
    } catch {
      // Ignore cache errors
    }
  };

  const checkSubscription = async (skipCache = false) => {
    try {
      // Try to load from cache first
      if (!skipCache) {
        const cached = loadFromCache();
        if (cached) {
          setIsPremium(cached.subscribed || false);
          setSubscription(cached);
          setLoading(false);
          // Still check server in background but don't block UI
          checkSubscription(true);
          return;
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        const emptyData = { subscribed: false };
        setIsPremium(false);
        setSubscription(null);
        saveToCache(emptyData);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error("Error checking subscription:", error);
        setIsPremium(false);
        setSubscription(null);
        
        // Only show error toast if not loading from cache (to avoid spamming on initial load)
        if (skipCache) {
          toast({
            variant: "destructive",
            title: "Subscription Check Failed",
            description: "Unable to verify your subscription status. Please refresh the page or try again later.",
          });
        }
      } else {
        const wasNotPremium = !isPremium;
        const isNowPremium = data.subscribed || false;
        
        // Trigger celebration if user just became premium
        if (wasNotPremium && isNowPremium && !loading) {
          setJustBecamePremium(true);
        }
        
        setIsPremium(isNowPremium);
        setSubscription(data);
        saveToCache(data);
      }
    } catch (error: any) {
      console.error("Error in checkSubscription:", error);
      setIsPremium(false);
      setSubscription(null);
      
      // Provide user-friendly error messages
      if (skipCache) {
        const errorMessage = error?.message || "Unknown error occurred";
        let description = "Please try refreshing the page.";
        
        if (errorMessage.includes("network") || errorMessage.includes("fetch")) {
          description = "Check your internet connection and try again.";
        } else if (errorMessage.includes("session") || errorMessage.includes("auth")) {
          description = "You may need to sign in again.";
        }
        
        toast({
          variant: "destructive",
          title: "Connection Error",
          description,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkSubscription();

    // Check subscription status every 5 minutes (cache duration)
    const interval = setInterval(() => checkSubscription(true), CACHE_DURATION);

    // Listen for auth state changes
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(() => {
      checkSubscription(true); // Skip cache on auth changes
    });

    return () => {
      clearInterval(interval);
      authSubscription.unsubscribe();
    };
  }, []);

  return {
    isPremium,
    loading,
    subscription,
    refetch: checkSubscription,
    justBecamePremium,
    dismissCelebration: () => setJustBecamePremium(false),
  };
};
